import { css } from '@emotion/react';
import {
  type FC, type FormEvent, useCallback, useEffect, useState,
} from 'react';
import { FormFieldType } from '~/_shared/utils/form/formValidation.helpers';
import { useFormFieldValidation } from '~/_shared/utils/form/useFormFieldValidation';
import { useTranslation } from '~/_shared/utils/hooks';
import { LoginFormEmailView } from './loginFormEmailView.component';
import { LoginFormOptionsView } from './loginFormOptionsView.component';

export const inputWrapperStyle = css({ marginBottom: 16 });

type LoginFormProps = {
  error: string;
  isLoading: boolean;

  onGoogleLogin?: () => void;
  onLegacyLogin?: () => void;
  onMicrosoftLogin?: () => void;
  onSubmit: (email: string, password: string, rememberMe: boolean) => void;
};

export const LoginFormComponent: FC<LoginFormProps> = (props) => {
  const [rememberMe, setRememberMe] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [t] = useTranslation();

  const { value: email, onBlur: onEmailBlur, onChange: onEmailChange, errors: emailErrors, resetErrors: resetEmailErrors, validateValue: validateEmailValue } = useFormFieldValidation({
    type: FormFieldType.Email,
  });
  const { value: password, onBlur: onPasswordBlur, onChange: onPasswordChange, errors: passwordErrors, validateValue: validatePasswordValue } = useFormFieldValidation({
    type: FormFieldType.Required,
    requiredFieldErrorMessage: t('Password must not be empty'),
  });

  const toggleRememberMe = useCallback(() => setRememberMe(value => !value), []);

  const onShowEmailForm = useCallback(() => {
    if (!validateEmailValue(email).length) {
      setShowEmailForm(true);
    }
  }, [email, validateEmailValue]);

  const onFormSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();

    if (!showEmailForm) {
      onShowEmailForm();
      return;
    }

    setShowError(false);

    const errors = [
      validateEmailValue(email),
      validatePasswordValue(password),
    ];

    if (errors.every(e => !e.length)) {
      const submit = props.onSubmit;
      submit(email, password, rememberMe);
    }
  }, [email, onShowEmailForm, password, props.onSubmit, rememberMe, showEmailForm, validateEmailValue, validatePasswordValue]);

  useEffect(() => {
    setShowError(!!props.error);
  }, [props.error]);

  const onBackToOptions = useCallback(() => {
    if (!email) {
      resetEmailErrors();
    }
    setShowEmailForm(false);
  }, [email, resetEmailErrors]);

  return (
    <form onSubmit={onFormSubmit}>
      {showEmailForm ? (
        <LoginFormEmailView
          email={{ value: email, errors: emailErrors, onBlur: onEmailBlur, onChange: onEmailChange }}
          error={props.error}
          isLoading={props.isLoading}
          onBack={onBackToOptions}
          onToggleRememberMe={toggleRememberMe}
          password={{ value: password, errors: passwordErrors, onBlur: onPasswordBlur, onChange: onPasswordChange }}
          rememberMe={rememberMe}
          showError={showError}
          userEmail={email}
        />
      ) : (
        <LoginFormOptionsView
          email={{ value: email, errors: emailErrors, onBlur: onEmailBlur, onChange: onEmailChange }}
          error={props.error}
          isLoading={props.isLoading}
          onLegacyLogin={props.onLegacyLogin}
          onShowEmailForm={onShowEmailForm}
          onToggleRememberMe={toggleRememberMe}
          rememberMe={rememberMe}
          showError={showError}
          sso={{ onGoogleLogin: props.onGoogleLogin, onMicrosoftLogin: props.onMicrosoftLogin }}
          userEmail={email}
        />
      )}
    </form>
  );
};
